import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useState } from 'react';
import { translateKey } from 'src/locales/translate';
import { useDemandProductOptions } from '../../hooks/useDemandProductOptions';
import { preventEnterSubmit } from '../../utils/forms';
import { withAutocompleteFieldInfo } from '../WarehouseFieldInfo';
import { DemandProductOption } from './WarehouseDemandRequestForm.schema';

const f = (key: string, options?: Record<string, unknown>): string =>
  translateKey(`warehouse.demand.form.${key}`, options);

/** The list's last option: the typed text as a product outside the Warehouse. */
interface OutsideNameOption {
  outsideName: string;
}

type PickerOption = DemandProductOption | OutsideNameOption;

const isOutsideName = (option: PickerOption): option is OutsideNameOption =>
  'outsideName' in option;

interface WarehouseDemandProductFieldProps {
  /** Searches only while the form is open (F-2). */
  enabled: boolean;
  disabled?: boolean;
  onPick: (product: DemandProductOption) => void;
  /** A product outside the Warehouse, by its trimmed name. */
  onPickName: (name: string) => void;
}

/**
 * "Dodaj produkt": the only entry of the request. It searches the Warehouse
 * on the server as the user types; the typed text itself is the last option,
 * a product outside the Warehouse, and Enter without a highlighted option
 * adds it too. It clears itself for the next one.
 */
export default function WarehouseDemandProductField({
  enabled,
  disabled = false,
  onPick,
  onPickName,
}: WarehouseDemandProductFieldProps): JSX.Element {
  const [search, setSearch] = useState('');
  const { options, isLoading } = useDemandProductOptions(
    search,
    enabled && !disabled,
  );

  const choose = (choice: PickerOption | string | null): void => {
    if (choice === null) {
      return;
    }
    if (typeof choice !== 'string' && !isOutsideName(choice)) {
      onPick(choice);
      setSearch('');
      return;
    }
    const name = (
      typeof choice === 'string' ? choice : choice.outsideName
    ).trim();
    if (name) {
      onPickName(name);
      setSearch('');
    }
  };

  return (
    <Autocomplete<PickerOption, false, false, true>
      freeSolo
      value={null}
      inputValue={search}
      options={options}
      loading={isLoading}
      disabled={disabled}
      autoHighlight
      onKeyDown={preventEnterSubmit}
      // The server already filtered the Products.
      filterOptions={(items, { inputValue }) => {
        const name = inputValue.trim();
        return name ? [...items, { outsideName: name }] : items;
      }}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option;
        }
        return isOutsideName(option)
          ? f('addOutsideProduct', { name: option.outsideName })
          : option.name;
      }}
      getOptionKey={(option) => {
        if (typeof option === 'string') {
          return option;
        }
        return isOutsideName(option)
          ? `outside:${option.outsideName}`
          : option.id;
      }}
      onInputChange={(_event, value, reason) => {
        if (reason !== 'reset') setSearch(value);
      }}
      onChange={(_event, choice) => choose(choice)}
      data-testid="warehouse-demand-product"
      renderInput={(params) => (
        <TextField
          {...params}
          label={f('addProduct')}
          InputProps={{
            ...params.InputProps,
            endAdornment: withAutocompleteFieldInfo(
              f('info.addProduct'),
              <>
                {isLoading && <CircularProgress color="inherit" size={16} />}
                {params.InputProps.endAdornment}
              </>,
            ),
          }}
        />
      )}
    />
  );
}
